import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";
import { ContractAlertTargetResource } from "@/interfaces";
import { FormInput } from "../FormInput";
import { useTranslation } from "react-i18next";
import useFormRequest from "@/hooks/useFormRequest";
import { FormSelect } from "../FormSelect";
import { Form } from "../ui/form";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { useState } from "react";
import { CONTRACT_ALERT_METHODS } from "@/constants";
import { normalizePhone } from "@/utils/phone";

const PHONE_METHODS = ["SMS", "CALL"];

export type ContactFormData = {
  fullName: string;
  relationType: string;
  phoneNumber: string;
  email: string;
  notifications: {
    phone: boolean;
    sms: boolean;
    email: boolean;
  };
};

export type AddOrEditContactModalProps = {
  row?: ContractAlertTargetResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId?: string;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
};

export function AddOrEditContactModal({
  row,
  open,
  onOpenChange,
  contractId,
  refetch
}: AddOrEditContactModalProps) {
  const { t } = useTranslation();
  const [addNew, setAddNew] = useState(false);
  const isEdit = !!row?.id;

  const initialPayload = {
    data: {
      ...(isEdit ? { id: String(row!.id) } : {}),
      type: "contract-alert-targets",
      attributes: {
        method: row?.attributes?.method ?? "",
        target: PHONE_METHODS.includes(row?.attributes?.method ?? "")
          ? normalizePhone(row?.attributes?.target)
          : row?.attributes?.target ?? "",
      },
      ...(contractId
        ? {
            relationships: {
              contract: { data: { type: "contracts", id: Number(contractId) } },
            },
          }
        : {}),
    },
  };
  
  const { loading, send, reset, form } = useFormRequest<
    typeof initialPayload,
    any
  >(
    `/folloportal/contract-alert-targets${isEdit ? "/" + row.id : ""}`,
    isEdit ? "PUT" : "POST",
    initialPayload,
    () => {
      reset();
      refetch();
      if (!addNew) {
        onOpenChange(false);
      }
    },
    undefined,
    {
      transform: (payload) =>
        PHONE_METHODS.includes(payload.data.attributes.method)
          ? {
              ...payload,
              data: {
                ...payload.data,
                attributes: {
                  ...payload.data.attributes,
                  target: normalizePhone(payload.data.attributes.target),
                },
              },
            }
          : payload,
    }
  );

  const method = form.watch("data.attributes.method");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-8">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader>
          <DialogTitle className="text-lg font-semibold mb-4">
            {row?.id ? t("models.alertContractModel.editContact") : t("models.alertContractModel.addContact")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={send}>
            <FormSelect
              className="mb-5 gap-2"
              form={form}
              label={t("models.alertContractModel.alerts.method")}
              placeholder={t("models.alertContractModel.alerts.methodPlaceholder")}
              name="data.attributes.method"
              options={
                CONTRACT_ALERT_METHODS.map((method) => ({
                  value: method,
                  label: t(
                    `models.alertContractModel.alerts.options.${method.toLowerCase()}`
                  ),
                }))
              }
            />
            <FormInput
              className="gap-2"
              form={form}
              type={method === "EMAIL" ? "email" : "text"}
              label={method === "EMAIL" 
                ? t("models.alertContractModel.email") 
                : t("models.alertContractModel.alerts.number")}
              name="data.attributes.target"
              placeholder={""}
              phone={PHONE_METHODS.includes(method)}
            />

            {/* TODO Keep this UI will be added later */}
            {/* <div className="md:space-y-6 md:mt-6">
              <FormInput
                className="mb-7 gap-1"
                form={form}
                label={t("models.alertContractModel.fullName")}
                name="attributes.contactName"
                placeholder=""
              />

              <FormSelect
                className="mb-4 gap-1"
                formFieldParams={formFieldParams}
                label={t("models.alertContractModel.relation")}
                name="attributes.relationType"
                placeholder=""
                options={relationsOptions}
              />

              <div className="grid md:grid-cols-2 md:gap-3">
                <FormInput
                  className="my-3 gap-1"
                  formFieldParams={formFieldParams}
                  label={t("models.alertContractModel.phone")}
                  name="attributes.phone"
                  placeholder=""
                />
                <FormInput
                  className="my-3 gap-1"
                  formFieldParams={formFieldParams}
                  label={t("models.alertContractModel.email")}
                  name="attributes.email"
                  placeholder=""
                />
              </div>

              <div className="space-y-3 pt-3">
                <div>
                  <h3 className="font-medium">
                    {t("models.alertContractModel.alerts.title")}{" "}
                    <span className="text-sm text-muted-foreground font-normal">
                      ({t("models.alertContractModel.alerts.availableAlerts", {
                        numberOfAlerts: 2,
                      })})
                    </span>
                  </h3>
                  <p className="text-sm text-foreground mt-2">
                    {t("models.alertContractModel.alerts.description")}
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <div className="flex items-center space-x-2 border rounded-lg px-4 py-2 h-12">
                    <Checkbox id="phone" />
                    <Label
                      htmlFor="phone"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Appel téléphonique
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 border rounded-lg px-4 py-2 h-12">
                    <Checkbox 
                      id={`sms-${row?.id}`}
                      checked={row?.attributes.method === "CALL"} 
                    />
                    <Label
                      htmlFor="sms"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Message texte (SMS)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 border rounded-lg px-4 py-2 h-12">
                    <Checkbox id="email-notif" />
                    <Label
                      htmlFor="email-notif"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Courriel (Email)
                    </Label>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Footer Actions */}
            <div className="flex items-center justify-between md:pt-9 pt-2">
              <Button
                variant="outline"
                className="min-w-[120px] cursor-pointer"
                onClick={() => onOpenChange(false)}
              >
                {t("ui.cancel")}
              </Button>
              <div className="flex md:flex-row flex-col items-center gap-4">
                {!isEdit && (
                  <>
                    <Checkbox  
                      checked={addNew}
                      onCheckedChange={(checked) => setAddNew(!!checked)} 
                      id="add-new" 
                      className="md:block hidden" 
                    />
                    <Label
                      htmlFor="add-new"
                      className="text-sm font-normal cursor-pointer md:block hidden"
                    >
                      {t("models.alertContractModel.addNewContact")}
                    </Label>
                  </>
                )}
                <Button disabled={loading} type="submit" className="min-w-[120px] cursor-pointer">
                  {loading ? t("ui.loading") : t("ui.save")}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
